import {
  EGlobalSearchItemType,
  EGlobalSearchTab,
} from "@frontend-shared/types";
import type { ParseKeys } from "i18next";
import type { LucideIcon } from "lucide-react";
import {
  Bell,
  Bot,
  Brain,
  Building2,
  CalendarDays,
  CircleHelp,
  Cpu,
  FileOutput,
  FileText,
  Folder,
  FolderKanban,
  KeyRound,
  LayoutGrid,
  Library,
  Mail,
  MessageSquare,
  MessagesSquare,
  NotebookPen,
  Puzzle,
  ScanText,
  Search,
  Settings,
  ShieldAlert,
  ShieldCheck,
  SlidersHorizontal,
  Sparkles,
  Store,
  UserRound,
  Video,
  Workflow,
  Wrench,
} from "lucide-react";
import type { CSSProperties } from "react";

// NOTE: Every item type has a group heading; a type added on the server
// before this map learns it falls back to its raw value (see GlobalSearchGroup).
export const GLOBAL_SEARCH_GROUP_TITLE_KEYS: Readonly<
  Record<EGlobalSearchItemType, ParseKeys<"globalSearch">>
> = {
  [EGlobalSearchItemType.ASSISTANT]: "groups.assistant",
  [EGlobalSearchItemType.PROMPT]: "groups.prompt",
  [EGlobalSearchItemType.CUSTOM_TOOL]: "groups.custom_tool",
  [EGlobalSearchItemType.CUSTOM_INTEGRATION]: "groups.custom_integration",
  [EGlobalSearchItemType.INTEGRATION_CONNECTION]:
    "groups.integration_connection",
  [EGlobalSearchItemType.SKILL]: "groups.skill",
  [EGlobalSearchItemType.CATALOG_SKILL]: "groups.catalog_skill",
  [EGlobalSearchItemType.AUTOMATION]: "groups.automation",
  [EGlobalSearchItemType.MARKETPLACE_AUTOMATION]:
    "groups.marketplace_automation",
  [EGlobalSearchItemType.MODEL]: "groups.model",
  [EGlobalSearchItemType.KNOWLEDGE_FILE]: "groups.knowledge_file",
  [EGlobalSearchItemType.MEETING]: "groups.meeting",
  [EGlobalSearchItemType.CALENDAR_EVENT]: "groups.calendar_event",
  [EGlobalSearchItemType.GENERATED_FILE]: "groups.generated_file",
  [EGlobalSearchItemType.DOC_RECOGNITION_DOCUMENT]:
    "groups.doc_recognition_document",
  [EGlobalSearchItemType.CHAT_FOLDER]: "groups.chat_folder",
  [EGlobalSearchItemType.MEMBER]: "groups.member",
  [EGlobalSearchItemType.INVITE]: "groups.invite",
  [EGlobalSearchItemType.ROLE]: "groups.role",
  [EGlobalSearchItemType.API_KEY]: "groups.api_key",
  [EGlobalSearchItemType.MEMORY_FACT]: "groups.memory_fact",
  [EGlobalSearchItemType.CUSTOM_INSTRUCTIONS]: "groups.custom_instructions",
  [EGlobalSearchItemType.PROBE]: "groups.probe",
  [EGlobalSearchItemType.CHAT]: "groups.chat",
  [EGlobalSearchItemType.CHAT_MESSAGE]: "groups.chat_message",
  [EGlobalSearchItemType.PAGE]: "groups.page",
  [EGlobalSearchItemType.SETTINGS_TAB]: "groups.settings_tab",
  [EGlobalSearchItemType.SETTINGS_OPTION]: "groups.settings_option",
  [EGlobalSearchItemType.INTEGRATION_PROVIDER]: "groups.integration_provider",
  [EGlobalSearchItemType.PROJECT]: "groups.project",
  [EGlobalSearchItemType.ORGANIZATION]: "groups.organization",
  [EGlobalSearchItemType.FAQ_ITEM]: "groups.faq_item",
  [EGlobalSearchItemType.NOTIFICATION]: "groups.notification",
  [EGlobalSearchItemType.ACTION]: "groups.action",
};

export const GLOBAL_SEARCH_ITEM_ICONS: Readonly<
  Record<EGlobalSearchItemType, LucideIcon>
> = {
  [EGlobalSearchItemType.ASSISTANT]: Bot,
  [EGlobalSearchItemType.PROMPT]: NotebookPen,
  [EGlobalSearchItemType.CUSTOM_TOOL]: Wrench,
  [EGlobalSearchItemType.CUSTOM_INTEGRATION]: Puzzle,
  [EGlobalSearchItemType.INTEGRATION_CONNECTION]: Puzzle,
  [EGlobalSearchItemType.SKILL]: Sparkles,
  [EGlobalSearchItemType.CATALOG_SKILL]: Library,
  [EGlobalSearchItemType.AUTOMATION]: Workflow,
  [EGlobalSearchItemType.MARKETPLACE_AUTOMATION]: Store,
  [EGlobalSearchItemType.MODEL]: Cpu,
  [EGlobalSearchItemType.KNOWLEDGE_FILE]: FileText,
  [EGlobalSearchItemType.MEETING]: Video,
  [EGlobalSearchItemType.CALENDAR_EVENT]: CalendarDays,
  [EGlobalSearchItemType.GENERATED_FILE]: FileOutput,
  [EGlobalSearchItemType.DOC_RECOGNITION_DOCUMENT]: ScanText,
  [EGlobalSearchItemType.CHAT_FOLDER]: Folder,
  [EGlobalSearchItemType.MEMBER]: UserRound,
  [EGlobalSearchItemType.INVITE]: Mail,
  [EGlobalSearchItemType.ROLE]: ShieldCheck,
  [EGlobalSearchItemType.API_KEY]: KeyRound,
  [EGlobalSearchItemType.MEMORY_FACT]: Brain,
  [EGlobalSearchItemType.CUSTOM_INSTRUCTIONS]: NotebookPen,
  [EGlobalSearchItemType.PROBE]: ShieldAlert,
  [EGlobalSearchItemType.CHAT]: MessageSquare,
  [EGlobalSearchItemType.CHAT_MESSAGE]: MessagesSquare,
  [EGlobalSearchItemType.PAGE]: LayoutGrid,
  [EGlobalSearchItemType.SETTINGS_TAB]: Settings,
  [EGlobalSearchItemType.SETTINGS_OPTION]: SlidersHorizontal,
  [EGlobalSearchItemType.INTEGRATION_PROVIDER]: Puzzle,
  [EGlobalSearchItemType.PROJECT]: FolderKanban,
  [EGlobalSearchItemType.ORGANIZATION]: Building2,
  [EGlobalSearchItemType.FAQ_ITEM]: CircleHelp,
  [EGlobalSearchItemType.NOTIFICATION]: Bell,
  [EGlobalSearchItemType.ACTION]: Search,
};

// NOTE: NSAI-6812 — a result whose destination has a sidebar entry wears that
// entry's glyph (NavigationIcon) instead of the type icon above. The sidebar
// draws it lighter; here it takes the stroke of the type icons beside it.
export const GLOBAL_SEARCH_NAVIGATION_ICON_STYLE: Readonly<CSSProperties> = {
  strokeWidth: 2,
};

// NOTE: The values a result's `params.badge` may carry (MEETING sends
// "uploaded"); each maps to a globalSearch:badges.* key.
export const GLOBAL_SEARCH_BADGE_KEYS: Readonly<
  Record<string, ParseKeys<"globalSearch">>
> = {
  uploaded: "badges.uploaded",
  shared: "badges.shared",
  public: "badges.public",
  personal: "badges.personal",
  semantic: "badges.semantic",
};

// NOTE: NSAI-6853 — the label of each category tab, typed for web's `t`. The
// same keys as the shared tab definitions' `labelKey`.
export const GLOBAL_SEARCH_TAB_LABEL_KEYS: Readonly<
  Record<EGlobalSearchTab, ParseKeys<"globalSearch">>
> = {
  [EGlobalSearchTab.ALL]: "tabs.all",
  [EGlobalSearchTab.CHATS]: "tabs.chats",
  [EGlobalSearchTab.PROJECTS]: "tabs.projects",
  [EGlobalSearchTab.ASSISTANTS]: "tabs.assistants",
  [EGlobalSearchTab.PROMPTS]: "tabs.prompts",
  [EGlobalSearchTab.SKILLS]: "tabs.skills",
  [EGlobalSearchTab.AGENTS]: "tabs.agents",
  [EGlobalSearchTab.GENERATED_FILES]: "tabs.generated_files",
  [EGlobalSearchTab.KNOWLEDGE]: "tabs.knowledge",
  [EGlobalSearchTab.MEETINGS]: "tabs.meetings",
  [EGlobalSearchTab.INTEGRATIONS]: "tabs.integrations",
  [EGlobalSearchTab.MODELS]: "tabs.models",
  [EGlobalSearchTab.PAGES]: "tabs.pages",
  [EGlobalSearchTab.SETTINGS]: "tabs.settings",
  [EGlobalSearchTab.ORGANIZATION_SETTINGS]: "tabs.organization_settings",
  [EGlobalSearchTab.HELP]: "tabs.help",
};

// NOTE: NSAI-6853 — every result row of the palette (items, chats, message
// hits) shares one shape: a soft rounded highlight on the glass, a muted
// glyph that brightens with the row, no dividers between rows.
export const GLOBAL_SEARCH_ROW_CLASS_NAME =
  "group gap-3 rounded-xl px-3 py-2 data-[selected='true']:bg-glass-fill-strong data-[selected=true]:text-foreground";

export const GLOBAL_SEARCH_ROW_ICON_CLASS_NAME =
  "flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-foreground-secondary transition-colors group-data-[selected=true]:text-foreground";
